import _ from 'lodash';
import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import React, { useState } from 'react';
import { Col, Input, Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';

import {
  CustomNotification,
  StyledModal,
  WrapperSelect,
} from 'common/components';
import { changeBreakout } from './utils';
import {
  getKeyProductIngredients,
  useMutationIngredients,
} from './useProductIngredients';
import { useInvalidateGroupDetail } from '../../shared/hooks';
import { useQueryClient } from '@tanstack/react-query';

import { queryKeyQaSpecIngredients } from 'pages/qa-spec/components/qa-ingredient/hook';
import { productFullViewKeys } from 'hooks';
import { keyGetProductItemModules } from 'pages/product-full-view/shared/hooks';
import { getQaPackagingTabDataQueryKey } from 'pages/qa-spec/components/qa-packaging/queries';
import { useCheckOverlaidList } from '../hook';
import { OverlayInputWrapper } from 'common/components/overlay';

import { useCheckIsProductOverlay } from 'hooks/product';
import Messages from 'i18n/messages/product';

function IngredientForm({
  visible,
  onReset,
  setVisible,
  ingredientsMode,
  currentIngredient,
}) {
  const intl = useIntl();
  const { id } = useParams();
  const { handleSyncAllProductDetailGroup } = useInvalidateGroupDetail(id);
  const queryClient = useQueryClient();

  const { index, ingredients, ingredientStatement, originData } =
    currentIngredient ?? {};

  const initStatement =
    ingredients?.join(', ') || ingredientStatement?.join(', ');

  const [statement, setStatement] = useState(initStatement);

  const mutation = useMutationIngredients();
  const { hasOverlaidList } = useCheckOverlaidList();

  const isProductOverlay = useCheckIsProductOverlay();

  const originIngredient =
    originData?.ingredients || originData?.ingredientStatement;

  const handleCancel = () => {
    setVisible(false);
  };

  const onFinish = () => {
    if (!statement?.length) {
      CustomNotification.error('Please specify ingredients statement value');
      return;
    }

    const params = {
      productId: id,
      ingredientGroups: [
        {
          ingredients: breakout,
          index: index ?? null,
          isRemoved: false,
        },
      ],
    };

    mutation.mutate(params, {
      onSuccess: async (result) => {
        if (result?.isSuccess) {
          if (ingredientsMode === 'add')
            CustomNotification.success('Add Ingredients successfully');
          else
            CustomNotification.success('Update Ingredient data successfully!');

          hasOverlaidList &&
            CustomNotification.info(
              intl.formatMessage(Messages.syncOverlayData)
            );
          onReset();
          handleSyncAllProductDetailGroup();
          queryClient.invalidateQueries({
            queryKey: getKeyProductIngredients(id),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeyQaSpecIngredients(id),
          });
          queryClient.invalidateQueries({
            queryKey: productFullViewKeys.itemData(id),
          });
          queryClient.invalidateQueries({
            queryKey: keyGetProductItemModules(id),
          });
          queryClient.invalidateQueries({
            queryKey: getQaPackagingTabDataQueryKey(id),
          });
          setVisible(false);
        } else {
          CustomNotification.error(result?.message || 'Something went wrong!');
        }
      },
      onError: (error) => {
        CustomNotification.error('Something went wrong!');
      },
    });
  };

  const handleRemoveIngredients = (value) => {
    const removeIngredient = breakout?.filter(
      (ingredient) => ingredient !== value
    );

    setStatement(removeIngredient);
  };

  const breakout = changeBreakout(statement);

  const modalProps = {
    title: ingredientsMode === 'add' ? 'Add Ingredient' : 'Edit Ingredient',
    visible: visible,
    width: '60%',
    closable: true,
    maskClosable: false,
    onCancel: handleCancel,
    wrapClassName: 'ingredient-modal',
    onOk: onFinish,
    okText: 'Submit',
  };

  const showOverlayField = ingredientsMode !== 'add';

  return (
    <div>
      <StyledModal
        {...modalProps}
        loadingContent={{ loadingText: 'Loading' }}
        confirmLoading={mutation.isLoading}
      >
        <Col span={24}>
          <p className='title-section'>
            Ingredients Statement
            <Tooltip title='Use ; or , to separating items'>
              <InfoCircleFilled style={{ marginLeft: '5px' }} />
            </Tooltip>
          </p>
          <OverlayInputWrapper
            value={breakout}
            checkShowOverlayFn={({ value }) => {
              return !isEqual(value, originIngredient) && isProductOverlay;
            }}
            originValue={originIngredient || 'N/A'}
            style={{ marginBottom: -20 }}
            wrapperStyle={{ marginBottom: 20 }}
            showOverlay={showOverlayField}
          >
            <Input.TextArea
              rows={5}
              maxLength={3000}
              onChange={(e) => {
                setStatement(e.target.value);
              }}
              value={statement}
              showCount
            />
          </OverlayInputWrapper>
        </Col>
        <Col span={24}>
          <p className='title-section'>Ingredients Breakout</p>
          <OverlayInputWrapper
            value={breakout}
            checkShowOverlayFn={({ value }) => {
              return !isEqual(value, originIngredient) && isProductOverlay;
            }}
            originValue={originIngredient || 'N/A'}
            showOverlay={showOverlayField}
          >
            <WrapperSelect
              className='select-ingredients'
              mode='tags'
              style={{ width: '100%' }}
              placeholder='Ingredients'
              name='ingredients'
              listItemHeight={10}
              listHeight={250}
              value={breakout}
              onDeselect={handleRemoveIngredients}
              showSearch={false}
              showArrow={false}
              dropdownStyle={{ display: 'none' }}
            />
          </OverlayInputWrapper>
        </Col>
      </StyledModal>
    </div>
  );
}

export default IngredientForm;
